import { z } from "zod";
import { RiskPreferenceV1Schema } from "../ai-policy";
import { CalendarDateSchema } from "../campaign-creation";
import {
  productAdvertisingEligibilitySchema,
  productEligibilityEvidenceFields,
} from "../product-listing";
import {
  createDefaultSmartCreationTargetingStrategyConfiguration,
  SmartCreationTargetingStrategyConfigurationSchema,
} from "./strategy-configuration";

export const SmartCreationStepSchema = z.enum([
  "products",
  "objectives",
  "strategy",
  "structure",
  "preview",
]);
export type SmartCreationStep = z.infer<typeof SmartCreationStepSchema>;

export const SmartCreationObjectiveSchema = z.enum([
  "drive_growth",
  "optimize_roas",
  "launch_product",
  "promotion_sales_boost",
]);
export type SmartCreationObjective = z.infer<typeof SmartCreationObjectiveSchema>;

export const SmartCreationPromotionDateRangeSchema = z
  .object({
    startDate: CalendarDateSchema,
    endDate: CalendarDateSchema,
  })
  .strict()
  .superRefine((range, ctx) => {
    if (range.startDate > range.endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Promotion start date must be on or before its end date",
        path: ["endDate"],
      });
    }
  });
export type SmartCreationPromotionDateRange = z.infer<typeof SmartCreationPromotionDateRangeSchema>;

export const SmartCreationProductSchema = z
  .object({
    sku: z.string().trim().min(1).max(200),
    asin: z
      .string()
      .trim()
      .regex(/^[A-Z0-9]{10}$/),
    variationGroupKey: z.string().trim().min(1).max(64),
    title: z.string().nullable(),
    imageUrl: z.string().url().nullable(),
    ...productEligibilityEvidenceFields,
    eligibility: productAdvertisingEligibilitySchema.default("unknown"),
  })
  .strict();
export type SmartCreationProduct = z.infer<typeof SmartCreationProductSchema>;

export const SmartCreationProductOrganizationSchema = z.enum(["campaign", "ad_group"]);
export type SmartCreationProductOrganization = z.infer<
  typeof SmartCreationProductOrganizationSchema
>;

/** Campaign Structures that can produce an executable Smart Creation DraftContent. */
export const SmartCreationStructureSchema = z.enum(["standard", "binary", "precise"]);
export type SmartCreationStructure = z.infer<typeof SmartCreationStructureSchema>;

const SmartCreationAvailableStructureSchema = z
  .object({
    structure: SmartCreationStructureSchema,
    availability: z.literal("available"),
  })
  .strict();

/**
 * Legacy cached evaluations may retain this availability-only Precise placeholder.
 */
export const SmartCreationPreciseStructureAvailabilitySchema = z
  .object({
    structure: z.literal("precise"),
    availability: z.literal("unavailable"),
    reason: z.literal("semantics_unverified"),
  })
  .strict();

export const SmartCreationStructureAvailabilitySchema = z.discriminatedUnion("availability", [
  SmartCreationAvailableStructureSchema,
  SmartCreationPreciseStructureAvailabilitySchema,
]);
export type SmartCreationStructureAvailability = z.infer<
  typeof SmartCreationStructureAvailabilitySchema
>;

const CanonicalPersistedSmartCreationDraftSchema = z
  .object({
    currentStep: SmartCreationStepSchema,
    products: z.array(SmartCreationProductSchema).max(500),
    productOrganization: SmartCreationProductOrganizationSchema,
    objective: SmartCreationObjectiveSchema.nullable(),
    riskPreference: RiskPreferenceV1Schema,
    aiPersonality: z.number().int().min(1).max(5).optional(),
    promotionDateRange: SmartCreationPromotionDateRangeSchema.nullable(),
    targetAcos: z.number().finite().min(0.01).max(2).nullable(),
    totalDailyBudget: z.number().finite().positive().max(1_000_000).nullable(),
    startDate: CalendarDateSchema,
    endDate: CalendarDateSchema.nullable(),
    strategy: SmartCreationTargetingStrategyConfigurationSchema,
    structure: SmartCreationStructureSchema,
  })
  .strict()
  .superRefine((draft, ctx) => {
    const indexesBySku = new Map<string, number>();
    for (const [index, product] of draft.products.entries()) {
      const duplicateOf = indexesBySku.get(product.sku);
      if (duplicateOf !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Duplicate SKU (already selected at index ${duplicateOf})`,
          path: ["products", index, "sku"],
        });
      } else {
        indexesBySku.set(product.sku, index);
      }
    }
    if (draft.objective !== "promotion_sales_boost" && draft.promotionDateRange) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Promotion dates are only valid for Promotion Sales Boost",
        path: ["promotionDateRange"],
      });
    }
    if (draft.endDate !== null && draft.endDate < draft.startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Campaign end date must be on or after its start date",
        path: ["endDate"],
      });
    }
  });

const CanonicalSmartCreationDraftSchema = CanonicalPersistedSmartCreationDraftSchema.superRefine(
  (draft, ctx) => {
    if (
      draft.promotionDateRange !== null &&
      draft.endDate !== null &&
      draft.promotionDateRange.endDate > draft.endDate
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Promotion end date must be on or before the Campaign end date",
        path: ["promotionDateRange", "endDate"],
      });
    }
  },
);

/**
 * Older browser configurations stored a nullable single `product`. Decode
 * those saved selections into the current configuration before calculation.
 */
function normalizePersistedSmartCreationDraft(value: unknown): unknown {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  const candidate = value as Record<string, unknown>;
  const { product: legacyProduct, ...withoutLegacyProduct } = candidate;
  const hasProducts = Object.hasOwn(candidate, "products");
  const hasProductOrganization = Object.hasOwn(candidate, "productOrganization");
  const hasRiskPreference = Object.hasOwn(candidate, "riskPreference");
  const hasAiPersonality = Object.hasOwn(candidate, "aiPersonality");
  const hasPromotionDateRange = Object.hasOwn(candidate, "promotionDateRange");
  const hasEndDate = Object.hasOwn(candidate, "endDate");
  const shouldNormalizeLegacyStrategy =
    candidate.strategy === undefined || candidate.strategy === "automatic";
  return {
    ...withoutLegacyProduct,
    products: hasProducts
      ? candidate.products
      : legacyProduct === null || legacyProduct === undefined
        ? []
        : [legacyProduct],
    productOrganization: hasProductOrganization ? candidate.productOrganization : "campaign",
    riskPreference: hasRiskPreference ? candidate.riskPreference : "balanced",
    aiPersonality: hasAiPersonality
      ? candidate.aiPersonality
      : candidate.riskPreference === "conservative"
        ? 1
        : candidate.riskPreference === "aggressive"
          ? 5
          : 3,
    promotionDateRange: hasPromotionDateRange ? candidate.promotionDateRange : null,
    endDate: hasEndDate ? candidate.endDate : null,
    strategy: shouldNormalizeLegacyStrategy
      ? {
          branches: createDefaultSmartCreationTargetingStrategyConfiguration().branches.map(
            (branch) => ({
              ...branch,
              // Historical automatic-only drafts enabled all four expressions.
              targets: branch.targets.map((target) => ({ ...target, included: true })),
            }),
          ),
        }
      : candidate.strategy,
  };
}

export const SmartCreationDraftSchema = z.preprocess(
  normalizePersistedSmartCreationDraft,
  CanonicalSmartCreationDraftSchema,
);
export type SmartCreationDraft = z.infer<typeof SmartCreationDraftSchema>;
